
import React, { useState } from 'react';
import { ShieldCheck, Scale, FileText, Building2, Globe, Database, Server, Lock, BarChart2 } from 'lucide-react';

type LegalTab = 'COMPANY' | 'PRIVACY' | 'COOKIES' | 'TERMS';

const Legal: React.FC = () => {
  const [activeTab, setActiveTab] = useState<LegalTab>('COMPANY');

  const renderContent = () => {
    switch (activeTab) {
      case 'COMPANY':
        return (
          <div className="animate-fade-in space-y-8">
             <div className="border-l-4 border-black pl-6 py-2">
                <h3 className="text-2xl font-serif font-bold mb-2">Informazioni Societarie</h3>
                <p className="text-gray-600">Dettagli dell'ente giuridico responsabile del progetto Cyberumanità.</p>
             </div>
             
             <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-gray-50 p-8 border border-gray-200">
                    <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Sviluppo & Gestione</div>
                    <div className="text-3xl font-serif font-bold mb-2">EXPORT 24</div>
                    <div className="text-sm font-serif italic text-gray-500 mt-2">
                        Digital Solutions & Cyber Intelligence Research
                    </div>
                </div>

                <div className="bg-white p-8 border border-gray-200 flex flex-col justify-center relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-black transform rotate-45 translate-x-8 -translate-y-8"></div>
                    <div className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">Contatti Ufficiali</div>
                    <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                        Per comunicazioni legali, esercizio dei diritti GDPR, questioni amministrative o relative alla proprietà intellettuale.
                    </p>
                    <a href="mailto:mind@cyberumanita.com" className="text-xl font-bold underline decoration-2 underline-offset-4 hover:text-gray-600 transition-colors">
                        mind@cyberumanita.com
                    </a>
                </div>
             </div>
          </div>
        );

      case 'PRIVACY':
        return (
          <div className="animate-fade-in font-sans text-gray-700 leading-relaxed space-y-8 pb-12">
            <div>
                <h3 className="text-3xl font-serif font-bold text-black mb-2">Informativa Privacy</h3>
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                    Informativa ai sensi dell'art. 13 del Regolamento (UE) 2016/679 (GDPR)
                </p>
            </div>

            <div className="prose prose-sm max-w-none prose-headings:font-serif prose-headings:font-bold prose-a:text-black">
                <p>
                    La presente informativa descrive le modalità di gestione del sito web <strong>Cyberumanità</strong> in riferimento al trattamento dei dati personali degli utenti che lo consultano.
                </p>

                <h4>1. Titolare del Trattamento</h4>
                <p>
                    Il Titolare del trattamento dei dati è <strong>EXPORT 24</strong>.<br/>
                    Contatto: <strong>mind@cyberumanita.com</strong>.
                </p>

                <h4>2. Dati Trattati e Finalità</h4>
                
                <h5 className="font-bold text-black mt-4">A. Dati di Navigazione e Analitici (Google Analytics)</h5>
                <p>
                    Utilizziamo <strong>Google Analytics 4 (GA4)</strong> per raccogliere informazioni statistiche in forma aggregata sull'utilizzo del sito (es. numero di visitatori, pagine più visitate, tempo di permanenza). 
                    <br/>Questi dati vengono trattati per legittimo interesse del Titolare al fine di migliorare il servizio e la sicurezza del sito. Gli indirizzi IP vengono anonimizzati di default da GA4.
                </p>

                <h5 className="font-bold text-black mt-4">B. Dati Volontari (Form Contatti)</h5>
                <p>
                    L'invio facoltativo di messaggi tramite form o email comporta l'acquisizione dell'indirizzo del mittente, necessario per rispondere alle richieste.
                </p>

                <h5 className="font-bold text-black mt-4">C. LocalStorage (Dati Tecnici)</h5>
                <p>
                    Alcune preferenze (es. modifiche all'Atlante in modalità offline) vengono salvate esclusivamente sul dispositivo dell'utente tramite LocalStorage e non vengono trasmesse ai nostri server.
                </p>

                <h4>3. Trasferimento Dati</h4>
                <p>
                    I dati raccolti tramite Google Analytics possono essere trasferiti su server di Google LLC negli Stati Uniti, in conformità con le clausole contrattuali standard e le decisioni di adeguatezza vigenti (Data Privacy Framework).
                </p>

                <h4>4. Diritti degli Interessati</h4>
                <p>
                    Gli utenti possono esercitare i diritti previsti dal GDPR (accesso, rettifica, cancellazione, opposizione) contattando il Titolare. È inoltre possibile disabilitare il tracciamento di Google Analytics installando l'<a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">apposito componente aggiuntivo</a> per il browser.
                </p>
            </div>
          </div>
        );

      case 'COOKIES':
        return (
          <div className="animate-fade-in space-y-8">
            <div>
                <h3 className="text-3xl font-serif font-bold text-black mb-2">Cookie Policy</h3>
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                    Utilizzo di tecnologie di tracciamento e archiviazione
                </p>
            </div>

            <div className="bg-gray-50 p-6 border-l-4 border-black text-sm text-gray-700 leading-relaxed">
                Questo sito utilizza cookie tecnici (necessari) e cookie analitici di terze parti (Google Analytics) per finalità statistiche.
            </div>

            <div className="space-y-6">
                
                {/* LOCAL STORAGE */}
                <div className="border border-gray-200 p-6 bg-white">
                    <div className="flex items-center gap-3 mb-4">
                        <Database size={20} className="text-black" />
                        <h4 className="font-bold font-serif text-lg">Archiviazione Locale (Tecnici)</h4>
                        <span className="bg-green-100 text-green-800 text-[10px] font-bold px-2 py-1 uppercase tracking-widest rounded">Necessario</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                        Dati salvati nel LocalStorage del browser per garantire le funzionalità dell'applicazione.
                    </p>
                    <ul className="text-xs space-y-2 text-gray-500 font-mono bg-gray-50 p-4 border border-gray-100">
                        <li className="flex justify-between border-b border-gray-200 pb-1">
                            <span className="font-bold">cyberumanita_db_v6_expanded</span>
                            <span>Cache offline dell'Atlante.</span>
                        </li>
                        <li className="flex justify-between border-b border-gray-200 pb-1 pt-1">
                            <span className="font-bold">sysop_daily_quota</span>
                            <span>Rate limiting per il terminale AI.</span>
                        </li>
                        <li className="flex justify-between border-b border-gray-200 pb-1 pt-1">
                            <span className="font-bold">cyber_cookie_consent</span>
                            <span>Preferenze consenso cookie.</span>
                        </li>
                    </ul>
                </div>

                {/* ANALYTICS */}
                <div className="border border-gray-200 p-6 bg-white hover:border-black transition-colors">
                    <div className="flex items-center gap-3 mb-4">
                        <BarChart2 size={20} className="text-blue-600" />
                        <h4 className="font-bold font-serif text-lg">Google Analytics (Terze Parti)</h4>
                        <span className="bg-blue-100 text-blue-800 text-[10px] font-bold px-2 py-1 uppercase tracking-widest rounded">Statistica</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                        Cookie utilizzati da Google per raccogliere informazioni anonime su come i visitatori utilizzano il nostro sito. Questi dati ci aiutano a migliorare l'esperienza utente.
                    </p>
                    <ul className="text-xs space-y-2 text-gray-500 font-mono bg-gray-50 p-4 border border-gray-100">
                        <li className="flex justify-between border-b border-gray-200 pb-1">
                            <span className="font-bold">_ga</span>
                            <span>Distingue gli utenti (Durata: 2 anni).</span>
                        </li>
                        <li className="flex justify-between border-b border-gray-200 pb-1 pt-1">
                            <span className="font-bold">_ga_J7VZ5Z399V</span>
                            <span>Mantiene lo stato della sessione (Durata: 2 anni).</span>
                        </li>
                    </ul>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-xs font-bold underline hover:text-blue-600">Privacy Policy di Google</a>
                    </div>
                </div>

            </div>

            <div className="pt-8 border-t border-gray-200 text-xs text-gray-500">
                <p className="font-bold mb-2 uppercase tracking-wide">Opt-Out</p>
                <p>
                    Puoi impedire a Google Analytics di raccogliere i tuoi dati installando il <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-black font-bold underline">Componente aggiuntivo del browser per la disattivazione di Google Analytics</a>.
                </p>
            </div>
          </div>
        );

      case 'TERMS':
        return (
          <div className="animate-fade-in prose prose-sm max-w-none font-sans text-gray-600 pb-12">
            <h3 className="text-3xl font-serif font-bold text-black mb-6">Termini di Servizio</h3>
            
            <h4>1. Accettazione dei Termini</h4>
            <p>
                Accedendo al sito Cyberumanità, l'utente accetta di essere vincolato dai presenti Termini di Servizio.
            </p>

            <h4>2. Licenza di Utilizzo</h4>
            <p>
                Salvo diversa indicazione, i contenuti pubblicati sono protetti da copyright di proprietà di <strong>EXPORT 24</strong>. È concesso il permesso di scaricare temporaneamente una copia dei materiali per la sola visualizzazione personale.
            </p>

            <h4>3. Esclusione di Responsabilità (Disclaimer)</h4>
            <p>
                Le informazioni contenute nell'Atlante sono a scopo puramente <strong>informativo ed educativo</strong>. Non costituiscono consulenza legale o tecnica professionale.
            </p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-white pt-32 px-6 md:px-12 pb-20">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <header className="mb-16 border-b-2 border-black pb-8">
             <div className="flex items-center gap-2 text-xs font-bold tracking-[0.3em] uppercase text-gray-400 mb-4 font-sans">
                <Scale size={14} />
                Ufficio Legale
            </div>
            <h1 className="text-5xl md:text-7xl font-serif leading-none tracking-tighter">Legal & Compliance</h1>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Sidebar Navigation */}
            <div className="lg:col-span-3">
                <nav className="flex flex-col space-y-1 sticky top-32">
                    <button 
                        onClick={() => setActiveTab('COMPANY')}
                        className={`text-left px-4 py-4 text-xs font-bold uppercase tracking-widest transition-all border-l-2 ${activeTab === 'COMPANY' ? 'border-black text-black bg-gray-50' : 'border-transparent text-gray-400 hover:text-black hover:border-gray-200'}`}
                    >
                        <div className="flex items-center gap-3">
                            <Building2 size={16} /> Dati Societari
                        </div>
                    </button>
                    <button 
                        onClick={() => setActiveTab('PRIVACY')}
                        className={`text-left px-4 py-4 text-xs font-bold uppercase tracking-widest transition-all border-l-2 ${activeTab === 'PRIVACY' ? 'border-black text-black bg-gray-50' : 'border-transparent text-gray-400 hover:text-black hover:border-gray-200'}`}
                    >
                        <div className="flex items-center gap-3">
                            <ShieldCheck size={16} /> Privacy Policy
                        </div>
                    </button>
                    <button 
                        onClick={() => setActiveTab('COOKIES')}
                        className={`text-left px-4 py-4 text-xs font-bold uppercase tracking-widest transition-all border-l-2 ${activeTab === 'COOKIES' ? 'border-black text-black bg-gray-50' : 'border-transparent text-gray-400 hover:text-black hover:border-gray-200'}`}
                    >
                        <div className="flex items-center gap-3">
                            <Globe size={16} /> Cookie Policy
                        </div>
                    </button>
                    <button 
                        onClick={() => setActiveTab('TERMS')}
                        className={`text-left px-4 py-4 text-xs font-bold uppercase tracking-widest transition-all border-l-2 ${activeTab === 'TERMS' ? 'border-black text-black bg-gray-50' : 'border-transparent text-gray-400 hover:text-black hover:border-gray-200'}`}
                    >
                        <div className="flex items-center gap-3">
                            <FileText size={16} /> Termini Servizio
                        </div>
                    </button>
                </nav>
            </div>

            {/* Content Area */}
            <div className="lg:col-span-9 min-h-[500px] border-l border-gray-100 pl-0 lg:pl-12">
                {renderContent()}
            </div>

        </div>
      </div>
    </div>
  );
};

export default Legal;
